import React, { useState } from 'react';
import {
  Button,
  ButtonColor,
} from '../../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import {
  AddDeviceModal,
  ExternalDeviceInfo,
} from '../modal-components/AddDeviceModal.tsx';
import styles from '../FormStyles.module.scss';
import {
  FailedRepairOrderDraftDto,
  ReasonForReturn,
  RepairOrderDraftListItemDto,
  RepairsOrderClient,
  RepairsOrderQuery,
} from 'services/api/api-client.ts';
import EditIcon from 'assets/icons/edit.svg?react';
import FileIcon from 'assets/icons/file-icon.svg?react';
import BinIcon from 'assets/icons/bin.svg?react';
import ErrorIcon from 'assets/icons/error-icon.svg?react';
import {
  getRepairReasonOptions,
  getSelectedReasonOptions,
} from '../repairReasonOptions.ts';
import { tDynamic } from '../../../../helpers/tDynamic.ts';
import { IconButton } from '../../../../components/uikit/IconButton.tsx';
import clsx from 'clsx';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { Trans } from 'react-i18next';

type AddDeviceFormProps = {
  onBackStep: () => void;
  handleSubmit: () => void;
  failedDrafts: FailedRepairOrderDraftDto[];
  onDraftUpdated: (draftId?: number) => void;
};

type RemoveDeviceInfo = {
  id: number | null;
  deviceName: string;
};

type DeviceModalInfo = {
  isOpen: boolean;
  deviceInfo: ExternalDeviceInfo;
};

export const AddDeviceForm = ({
  onBackStep,
  handleSubmit,
  failedDrafts,
  onDraftUpdated,
}: AddDeviceFormProps) => {
  const i18n = useScopedTranslation('Page.CreateRepairRequest');
  const [deviceModalInfo, setDeviceModalInfo] = useState<DeviceModalInfo>({
    isOpen: false,
    deviceInfo: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [removeDeviceInfo, setRemoveDeviceInfo] = useState<RemoveDeviceInfo>({
    id: null,
    deviceName: '',
  });

  const drafts = RepairsOrderQuery.useGetUserOpenRepairOrderDraftsQuery();
  const draftItems = drafts.data?.drafts ?? [];
  const hasDrafts = !!draftItems.length;

  const reasonOptions = getRepairReasonOptions((key) => tDynamic(i18n, key));

  const getReasonsLabels = (reasonsForReturn: ReasonForReturn[]) => {
    const result: string[] = [];

    for (const reason of reasonsForReturn) {
      const label = reasonOptions
        .find((item) => item.key === reason)
        ?.text?.split('(')
        ?.at(0)
        ?.trim();

      if (label) {
        result.push(label);
      }
    }

    return result.join(', ') || '-';
  };

  const handleAddDevice = () => {
    setDeviceModalInfo({
      isOpen: true,
      deviceInfo: null,
    });
  };

  const handleEditDevice = (draft: RepairOrderDraftListItemDto) => {
    const {
      deviceDescription,
      additionalErrorDescription,
      serialNumber,
      selectedErrors,
      salesReceiptFileName,
      salesReceiptFileId,
      reasonsForReturn,
      replacementAlreadyReceived,
      workRequiredImmediately,
      ticketId,
      repairLimitEuro,
      article,
      deviceName,
      serviceProduct,
      id,
    } = draft;

    setDeviceModalInfo({
      isOpen: true,
      deviceInfo: {
        id: id,
        serialNumber: serialNumber ?? '',
        workRequiredImmediately: workRequiredImmediately ?? false,
        repairLimitEuro: String(repairLimitEuro || ''),
        ticketId: ticketId ?? '',
        errorDescription: additionalErrorDescription ?? '',
        deviceDescription: deviceDescription ?? '',
        replacementReceived: replacementAlreadyReceived ?? null,
        selectedFoundDevice: {
          serialNumber: serialNumber ?? '',
          article: article ?? '',
          deviceName: deviceName ?? '',
          text: '',
          key: serviceProduct ?? '',
        },
        receiptFile: null,
        receiptFileName: salesReceiptFileName ?? null,
        receiptFileId: salesReceiptFileId ?? null,
        selectedReasons: getSelectedReasonOptions(reasonsForReturn, (key) =>
          tDynamic(i18n, key),
        ),
        selectedErrors: selectedErrors,
      },
    });
  };

  const handleAddDeviceNextStep = (draftId?: number) => {
    onDraftUpdated(draftId);
    void drafts.refetch();
  };

  const showRemoveDeviceModal = (id: number, deviceName: string) => {
    setRemoveDeviceInfo({
      id,
      deviceName,
    });
  };

  const hideRemoveDeviceModal = () => {
    setRemoveDeviceInfo({
      id: null,
      deviceName: '',
    });
  };

  const handleDeleteDevice = async (id: number | null) => {
    if (!id) {
      return;
    }

    try {
      setIsLoading(true);

      await RepairsOrderClient.deleteRepairOrderDraft(id);
      await drafts.refetch();
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = !hasDrafts;

  return (
    <>
      <Loading loading={drafts.isLoading || isLoading}>
        <div>
          <div className={styles.sectionHeader}>
            <div className={styles.formTitle}>{i18n.t('form_title')}</div>
            <div className={styles.sectionSubtitle}> {i18n.t('step2')}</div>
          </div>
        </div>

        <div className={styles.stepTwoBody}>
          {!hasDrafts ? (
            <>
              <Button
                title={`+ ${i18n.t('add_device')}`}
                color={ButtonColor.Secondary}
                className={styles.addDeviceButton}
                onClick={handleAddDevice}
                testId={'add-device-button'}
              />
              <div className={styles.addDeviceHint}>
                {i18n.t('add_device_hint')}
              </div>
            </>
          ) : (
            <>
              <div className={styles.deviceDraftList}>
                {draftItems.map((draft) => {
                  const isDeviceNotFound = !draft.deviceName;
                  const submitError = failedDrafts.find(
                    (failedDraft) => failedDraft.draftId === draft.id,
                  )?.error;

                  return (
                    <div key={draft.id} className={styles.deviceDraftCard}>
                      <div className={styles.deviceDraftHeaderRow}>
                        <div
                          className={clsx(
                            styles.rowContainer,
                            styles.spaceBetween,
                            !isDeviceNotFound && styles.marginBottom6,
                          )}
                        >
                          <div
                            className={clsx(
                              styles.rowContainer,
                              styles.columnGap4,
                            )}
                          >
                            {isDeviceNotFound && (
                              <div
                                className={clsx(
                                  styles.deviceDraftDetailLabel,
                                  styles.deviceDraftTitle,
                                )}
                              >
                                {i18n.t('modal_sn')}
                              </div>
                            )}
                            <div className={styles.deviceDraftTitle}>
                              {draft.deviceName || draft.serialNumber || '-'}
                            </div>
                          </div>
                          <div className={styles.deviceDraftActions}>
                            <IconButton
                              type={'primary'}
                              iconClassName={styles.actionIcon}
                              onClick={() => handleEditDevice(draft)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              type={'destroy'}
                              iconClassName={styles.actionIcon}
                              onClick={() =>
                                showRemoveDeviceModal(
                                  draft.id,
                                  draft.deviceName ?? '',
                                )
                              }
                            >
                              <BinIcon />
                            </IconButton>
                          </div>
                        </div>
                        {!isDeviceNotFound && (
                          <div className={styles.deviceDraftMeta}>
                            <div
                              className={clsx(
                                styles.rowContainer,
                                styles.columnGap4,
                              )}
                            >
                              <div className={styles.deviceDraftDetailLabel}>
                                {i18n.t('modal_sn')}
                              </div>
                              <div className={styles.text}>
                                {draft.serialNumber || '-'}
                              </div>
                            </div>
                            <div
                              className={clsx(
                                styles.rowContainer,
                                styles.columnGap4,
                              )}
                            >
                              <div className={styles.deviceDraftDetailLabel}>
                                {i18n.t('modal_article')}
                              </div>
                              <div className={styles.text}>
                                {draft.article || '-'}
                              </div>
                            </div>
                          </div>
                        )}
                        {submitError && (
                          <div className={styles.deviceDraftError}>
                            <div>
                              <ErrorIcon />
                            </div>
                            <div>{submitError}</div>
                          </div>
                        )}
                      </div>
                      <div className={styles.deviceDraftDetailsRow}>
                        <div className={styles.deviceDraftDetailCell}>
                          <div className={styles.deviceDraftDetailLabel}>
                            {i18n.t(
                              isDeviceNotFound
                                ? 'modal_device_description'
                                : 'modal_error_name',
                            )}
                          </div>
                          <div className={styles.deviceDraftDetailValue}>
                            {isDeviceNotFound
                              ? draft.deviceDescription || '-'
                              : draft.selectedErrors
                                  .map((error) => error.text)
                                  .join(', ') || '-'}
                          </div>
                        </div>
                        <div className={styles.deviceDraftDetailCell}>
                          <div className={styles.deviceDraftDetailLabel}>
                            {i18n.t(
                              isDeviceNotFound
                                ? 'modal_error_description'
                                : 'modal_reason_for_return',
                            )}
                          </div>
                          <div className={styles.deviceDraftDetailValue}>
                            {isDeviceNotFound
                              ? draft.additionalErrorDescription || '-'
                              : getReasonsLabels(draft.reasonsForReturn)}
                          </div>
                        </div>
                      </div>

                      {draft.salesReceiptFileName && (
                        <div className={styles.fileBlock}>
                          <FileIcon />
                          <div className={styles.deviceDraftDetailLabel}>
                            {draft.salesReceiptFileName}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleAddDevice}
                className={styles.addDeviceTextButton}
                data-test-id={'add-device-button'}
              >
                + {i18n.t('add_device')}
              </button>
            </>
          )}
        </div>

        <div className={styles.stepTwoFooter}>
          <Button
            title={i18n.t('back')}
            color={ButtonColor.Secondary}
            onClick={onBackStep}
            testId={'create-repair-request-back'}
          />
          <Button
            title={i18n.t('submit')}
            color={ButtonColor.Primary}
            disabled={isSubmitDisabled}
            testId={'create-repair-request-final-submit'}
            onClick={handleSubmit}
          />
        </div>

        <AddDeviceModal
          isOpen={deviceModalInfo.isOpen}
          onClose={() =>
            setDeviceModalInfo({
              isOpen: false,
              deviceInfo: null,
            })
          }
          onSubmit={handleAddDeviceNextStep}
          defaultFormData={deviceModalInfo.deviceInfo}
        />

        {!!removeDeviceInfo.id && (
          <CustomModal
            isOpen={true}
            onSubmit={() => {
              hideRemoveDeviceModal();
              void handleDeleteDevice(removeDeviceInfo.id);
            }}
            onCancel={hideRemoveDeviceModal}
            cancelButtonText={i18n.t('cancel')}
            submitButtonText={i18n.t('remove_device')}
            titleText={i18n.t('remove_device')}
            containerClassName={styles.modalWidth480}
            textClassName={styles.lineHeight24}
            submitButtonType={ButtonColor.Warning}
            cancelButtonType={ButtonColor.Secondary}
          >
            <Trans
              i18nKey={'Page.CreateRepairRequest.remove_confirm_text'}
              values={{ device_name: removeDeviceInfo.deviceName }}
              components={{ bold: <strong /> }}
            />
          </CustomModal>
        )}
      </Loading>
    </>
  );
};
